// src/component/DiscountStrip.jsx
import { useEffect, useMemo, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.mogym.ir';

const palette = {
  purpleBg: '#581c87',
  purpleBorder: 'rgba(216,180,254,0.22)',

  blueBg: '#1e3a8a',
  blueBorder: 'rgba(147,197,253,0.22)',

  greenBg: '#14532d',
  greenBorder: 'rgba(134,239,172,0.22)',

  defaultText: '#FFFFFF',
  subText: 'rgba(255,255,255,0.82)',
  dot: 'rgba(255,255,255,0.32)',
  dotActive: '#FFFFFF',
};

function normalizeOffersResponse(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.result)) return data.result;
  if (Array.isArray(data?.Result)) return data.Result;
  return [];
}

function getThemeFromOffer(offer) {
  const bg = String(offer?.bg || offer?.Bg || '').toLowerCase();
  const border = String(offer?.border || offer?.Border || '').toLowerCase();

  if (bg.includes('green') || border.includes('green')) {
    return {
      bgColor: palette.greenBg,
      borderColor: palette.greenBorder,
    };
  }

  if (bg.includes('blue') || border.includes('blue')) {
    return {
      bgColor: palette.blueBg,
      borderColor: palette.blueBorder,
    };
  }

  return {
    bgColor: palette.purpleBg,
    borderColor: palette.purpleBorder,
  };
}

export default function DiscountStrip() {
  const [offers, setOffers] = useState([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function loadOffers() {
      try {
        setLoading(true);

        const res = await fetch(`${API_URL}/api/offers`);

        if (!res.ok) {
          if (!cancelled) setOffers([]);
          return;
        }

        const data = await res.json();
        const normalized = normalizeOffersResponse(data);

        if (!cancelled) {
          setOffers(normalized);
          setCurrent(0);
        }
      } catch (e) {
        console.log('offers error', e);
        if (!cancelled) setOffers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadOffers();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (offers.length <= 1) return;

    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % offers.length);
    }, 6000);

    return () => clearInterval(timer);
  }, [offers.length]);

  const offer = useMemo(() => {
    if (!offers.length) return null;
    return offers[current] || offers[0];
  }, [offers, current]);

  if ((loading && !offer) || !offer) return null;

  const title = offer?.title || offer?.Title || '';
  const description = offer?.description || offer?.Description || '';
  const hasDescription = !!String(description).trim();

  if (!title && !hasDescription) return null;

  const { bgColor, borderColor } = getThemeFromOffer(offer);

  return (
    <div
      dir="rtl"
      className="
        relative z-50
        mx-4 mt-2
        overflow-hidden
        rounded-2xl border
        shadow-lg
      "
      style={{
        backgroundColor: bgColor,
        borderColor,
      }}
    >
      <div
        className="
          min-h-[52px]
          px-4 py-2.5
          flex items-center justify-between gap-3
        "
      >
        <div className="flex-1 min-w-0 flex items-center gap-3">
          <div
            className="
              hidden sm:flex
              w-8 h-8 rounded-full
              items-center justify-center
              bg-white/10
              text-white
              shrink-0
            "
          >
            ✦
          </div>

          <div className="flex-1 min-w-0 text-right">
            <div
              className="
                text-white
                font-bold
                text-[13px] sm:text-sm
                leading-6
                line-clamp-2
              "
            >
              {title}
            </div>

            {hasDescription && (
              <div
                className="
                  mt-0.5
                  text-[11px] sm:text-xs
                  leading-5
                  truncate
                "
                style={{ color: palette.subText }}
              >
                {description}
              </div>
            )}
          </div>
        </div>

        {offers.length > 1 && (
          <div className="flex items-center justify-end gap-1 shrink-0">
            {offers.map((o, idx) => (
              <button
                key={o?.id ?? o?.Id ?? idx}
                type="button"
                onClick={() => setCurrent(idx)}
                className="
                  w-5 h-5
                  flex items-center justify-center
                  rounded-full
                "
                aria-label={`offer-${idx + 1}`}
              >
                <span
                  className="rounded-full transition-all"
                  style={{
                    width: idx === current ? 8 : 6,
                    height: idx === current ? 8 : 6,
                    backgroundColor:
                      idx === current ? palette.dotActive : palette.dot,
                  }}
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}